import { Agent, Channel, MerchantStructure } from '@nemuru-eng/js-api-client';
import { createContext, useContext, useReducer } from 'react';

import { PricingsListEntity } from '@/components/modules/PricingsList/entities/PricingsListEntity';
import { AlertNotification, AppRole, SnackbarNotification } from '@/config/types';
import { UserEntity } from '@/entities/UserEntity';
import UserInMerchantStructure from '@/entities/UserInMerchantStructure';

export interface UserContextState {
  updateUserContext: (newContext: Partial<UserContextState>) => void;
  agent?: Agent;
  loading: boolean;
  authError: null | any;
  appStarted: boolean;
  userEntity?: UserEntity | UserInMerchantStructure;
  structure?: MerchantStructure;
  currentChannel?: Channel;
  currentAgent?: Agent;
  snackbarNotification?: SnackbarNotification;
  alertNotification?: AlertNotification;
  roleOverride?: AppRole;
  pricingsListEntity?: PricingsListEntity;
}

const UserContext = createContext<UserContextState | null>(null);

const reducer = (state: UserContextState, newState: Partial<UserContextState>) => ({ ...state, ...newState });

const useUserReducer = () => {
  // @ts-expect-error null is a valid value
  const [state, dispatch] = useReducer(reducer, null);
  // @ts-expect-error dispatch also accepts an argument
  const updateUserContext = (newContext: Partial<UserContextState>) => dispatch(newContext);
  // This returns the state and the actions
  return { state, updateUserContext };
};

const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { state, updateUserContext } = useUserReducer();
  return <UserContext.Provider value={{ ...state, updateUserContext }}>{children}</UserContext.Provider>;
};

const useUserContext = (): UserContextState => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useContext must be used within a UserContextProvider');
  }
  return context;
};

export { UserContextProvider, useUserContext };
